import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NavigationPanel = ({ darkMode, setDarkMode }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <>
      {/* -- Top Nav Bar -- */}
      <nav
        className={`
          py-4 px-4
          transition-colors
          ${darkMode ? 'bg-gray-800 text-gray-100' : 'bg-gray-100 text-gray-800'}
        `}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Left side: (Mobile) Hamburger + Title  |  (Desktop) Just Title */}
          <div className="flex items-center space-x-2">
            {/* Hamburger button (visible on mobile only) */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            {/* Brand / Title */}
            <div
              className={
                darkMode
                  ? `
                      text-3xl font-bold
                      bg-gradient-to-r from-white via-[#b9f2ff] to-white
                      bg-[length:200%_auto] bg-clip-text text-transparent
                      animate-shine
                    `
                  : `
                      text-3xl font-bold
                      bg-gradient-to-r from-gray-600 via-gray-400 to-gray-600
                      bg-[length:200%_auto] bg-clip-text text-transparent
                      animate-shine
                    `
              }
            >
              Tidy Policy
            </div>
          </div>

          {/* Right side: Desktop navigation + Dark mode toggle */}
          <div className="flex items-center space-x-6">
            {/* Desktop Nav Links (hidden on mobile) */}
            <div className="hidden md:flex items-center space-x-6">
              <Link
                to="/"
                className={`
                  hover:underline
                  ${location.pathname === '/' ? 'text-[#FF9900] font-semibold' : ''}
                `}
              >
                Sort AWS IAM Policy
              </Link>
              <Link
                to="/scan"
                className={`
                  hover:underline
                  ${location.pathname === '/scan' ? 'text-[#FF9900] font-semibold' : ''}
                `}
              >
                Analyze AWS IAM Policy
              </Link>
              <Link
                to="/pci"
                className={`
                  hover:underline
                  ${location.pathname === '/pci' ? 'text-[#FF9900] font-semibold' : ''}
                `}
              >
                PCI-DSS Analyzer
              </Link>
              <Link
                to="/aiexplain"
                className={`
                  hover:underline
                  ${location.pathname === '/aiexplain' ? 'text-[#FF9900] font-semibold' : ''}
                `}
              >
                AI Policy Explainer
              </Link>
            </div>

            {/* Dark/Light Mode Switch (always visible) */}
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={darkMode}
                onChange={() => setDarkMode(!darkMode)}
              />
              <div
                className={`
                  w-11 h-6 rounded-full peer
                  peer-focus:outline-none
                  transition-colors
                  bg-gray-300 dark:bg-gray-600
                  peer-checked:bg-[#FF9900]
                  relative
                  after:content-['']
                  after:absolute
                  after:top-[2px]
                  after:left-[2px]
                  after:bg-white
                  after:border-gray-300
                  after:border
                  after:rounded-full
                  after:h-5
                  after:w-5
                  after:transition-all
                  peer-checked:after:translate-x-full
                  peer-checked:after:border-white
                `}
              />
              <span className="ml-3 text-sm font-medium">
                {darkMode ? 'Dark' : 'Light'}
              </span>
            </label>
          </div>
        </div>
      </nav>

      {/* -- Mobile Side Panel (slides from the left) -- */}
      <div
        className={`
          fixed top-0 left-0 h-full w-64 z-50
          transform transition-transform duration-300
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          ${darkMode ? 'bg-gray-800 text-gray-100' : 'bg-gray-100 text-gray-800'}
        `}
      >
        {/* Close Button */}
        <button
          type="button"
          className="p-4"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Close menu"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Mobile Nav Links */}
        <nav className="flex flex-col px-4 space-y-4 mt-2">
          <Link
            to="/"
            onClick={() => setIsMobileMenuOpen(false)}
            className={`
              hover:underline
              ${location.pathname === '/' ? 'text-[#FF9900] font-semibold' : ''}
            `}
          >
            Sort AWS IAM Policy
          </Link>
          <Link
            to="/scan"
            onClick={() => setIsMobileMenuOpen(false)}
            className={`
              hover:underline
              ${location.pathname === '/scan' ? 'text-[#FF9900] font-semibold' : ''}
            `}
          >
            Analyze AWS IAM Policy
          </Link>
          <Link
            to="/pci"
            onClick={() => setIsMobileMenuOpen(false)}
            className={`
              hover:underline
              ${location.pathname === '/pci' ? 'text-[#FF9900] font-semibold' : ''}
            `}
          >
            PCI-DSS Analyzer
          </Link>
          <Link
            to="/aiexplain"
            onClick={() => setIsMobileMenuOpen(false)}
            className={`
              hover:underline
              ${location.pathname === '/aiexplain' ? 'text-[#FF9900] font-semibold' : ''}
            `}
          >
            AI Policy Explainer
          </Link>
        </nav>
      </div>

      {/* -- Overlay to close the panel by clicking outside -- */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

export default NavigationPanel;